/** What this machine is doing right now, read from the agent's own process. */

import {
  Banner,
  DataList,
  EmptyState,
  Grid,
  MetricTile,
  Panel,
  Skeleton,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { AgentProblem } from "./AgentProblem";
import { NONE, cloudTone, duration, orNone, wallClock, yesNo } from "./format";
import { MachineAccountNotice } from "./MachineAccountNotice";
import { useAgentPairing, useAgentStatus, useServedSpaceName } from "./queries";

export function AgentStatus() {
  const status = useAgentStatus();
  const pairing = useAgentPairing();
  const servedSpace = useServedSpaceName(status.data?.cloud?.space_id);

  if (status.isPending) {
    return <Skeleton height="14rem" />;
  }
  if (status.isError) {
    return (
      <AgentProblem
        error={status.error}
        onRetry={() => void status.refetch()}
      />
    );
  }

  const data = status.data;
  const cloud = data.cloud;
  const connected = cloud?.status === "connected";

  return (
    <Stack gap={5}>
      <MachineAccountNotice machineId={pairing.data?.machine_id} />

      {connected ? null : (
        <Banner
          tone={cloud?.status === "reconnecting" ? "warning" : "danger"}
          title={`Cloud ${cloud?.status ?? "not connected"}`}
        >
          <Stack direction="row" gap={5} wrap>
            <span>Queued {data.submissions_pending}</span>
            <span>Attempts {cloud?.attempts ?? 0}</span>
            <span>Next retry {wallClock(cloud?.retry_at)}</span>
          </Stack>
        </Banner>
      )}

      <div aria-live="polite">
        <Grid minColumnWidth="9rem" gap={4}>
          <MetricTile value={duration(data.uptime_seconds)} label="Uptime" />
          <MetricTile value={data.submissions_pending} label="Queued" />
          <MetricTile
            value={`${data.clock_offset_ms} ms`}
            label="Clock offset"
          />
          <MetricTile value={data.events_published} label="Events" />
          <MetricTile value={data.handler_invocations} label="Handlers" />
        </Grid>
      </div>

      <Panel title="Cloud">
        {cloud === null ? (
          <EmptyState bare headline="Not connected" />
        ) : (
          <DataList
            items={[
              {
                id: "control-plane",
                title: "Control plane",
                badge: (
                  <StatusBadge tone={cloudTone(cloud.status)}>
                    {orNone(cloud.status)}
                  </StatusBadge>
                ),
                fields: [
                  {
                    label: "Space",
                    value: servedSpace ?? orNone(cloud.space_id),
                    mono: servedSpace === null,
                  },
                  { label: "Reason", value: orNone(cloud.reason) },
                  { label: "Next retry", value: wallClock(cloud.retry_at) },
                  { label: "Attempts", value: cloud.attempts ?? 0 },
                  { label: "Handshakes", value: cloud.handshakes ?? 0 },
                  { label: "Sent", value: cloud.sent ?? 0 },
                  { label: "Acknowledged", value: cloud.acknowledged ?? 0 },
                  { label: "Projections", value: cloud.projections ?? 0 },
                  { label: "Applied", value: cloud.applied ?? 0 },
                ],
              },
            ]}
          />
        )}
      </Panel>

      <Panel title="This machine">
        <DataList
          items={[
            {
              id: "process",
              title: "Process",
              badge: <StatusBadge tone="neutral">{data.status}</StatusBadge>,
              fields: [
                { label: "Environment", value: data.environment },
                { label: "Paired", value: yesNo(data.paired) },
                { label: "Clock synced", value: yesNo(data.clock_synced) },
                { label: "Tracker", value: orNone(data.tracker) },
                { label: "Gaze mode", value: orNone(data.gaze_mode) },
                { label: "Mirror", value: orNone(data.mirror_status) },
                { label: "Activities", value: data.mirror_activities },
                { label: "Entities", value: data.mirror_entities },
                { label: "Subscribers", value: data.bus_subscribers },
                {
                  label: "Blocking",
                  value:
                    data.overlay_blocking.length === 0
                      ? NONE
                      : data.overlay_blocking.join(", "),
                },
              ],
            },
          ]}
        />
      </Panel>
    </Stack>
  );
}
